import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Step,
  StepButton,
  Stepper,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  POSSIBLY_RELATED_NOTICE,
  REFERRAL_INCOMPLETE,
  REFERRAL_STEPS,
  recordNoun,
  referralCopy,
  referralIssuesBefore,
  referralSubmitIssues,
  type ReferralDto,
  type ReferralFormDto,
} from '@hv/shared';
import { ApiError, get } from '../api/client';
import {
  keys,
  useCheckReferralDuplicates,
  useMyAllotment,
  useMyReferral,
  useReferralForm,
  useSubmitReferral,
} from '../api/hooks';
import { useAuth } from '../components/AuthContext';
import { describeError, useNotify } from '../components/Notify';
import {
  allotmentFor,
  existingFamilyFrom,
  formFromReferral,
  newDraftForm,
  pruneBlankChildren,
  toRequest,
  type DraftForm,
} from './draft';
import {
  ApplicantsStep,
  FamilyStep,
  ReferralQuestionsStep,
  ReviewStep,
  TrusteeStep,
  type StepProps,
} from './ReferralSteps';
import { ReferralSubmissionView } from './ReferralSubmission';
import { useDraftSaver, type SaveState } from './useDraftSaver';

const STEP_VIEWS = [TrusteeStep, FamilyStep, ApplicantsStep, ReferralQuestionsStep, ReviewStep];
const RELATED_CHECK_STEPS = [2, 3];
const LAST_STEP = REFERRAL_STEPS.length;

const page = { p: 2, maxWidth: 960, mx: 'auto', width: '100%' } as const;

export function ReferralEditorPage() {
  const { id = 'new' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [session, setSession] = useState({ location: location.key, editor: location.key });
  if (session.location !== location.key)
    setSession({
      location: location.key,
      editor: id === createdId ? session.editor : location.key,
    });
  const fresh = id === 'new' || id === createdId;
  const config = useReferralForm();
  const referral = useMyReferral(fresh ? null : id);

  if (config.isPending || (!fresh && referral.isPending)) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress aria-label="Loading" />
      </Box>
    );
  }
  const error = config.error ?? (fresh ? null : referral.error);
  if (error || !config.data) {
    const missing = error instanceof ApiError && error.status === 404;
    return (
      <Box sx={page}>
        <Alert
          severity={missing ? 'warning' : 'error'}
          action={
            <Button component={RouterLink} to="/my-referrals">
              My Referrals
            </Button>
          }
        >
          {missing
            ? 'This referral was not found. It may have been deleted.'
            : `Could not load the referral form: ${describeError(error)}`}
        </Alert>
      </Box>
    );
  }
  if (!fresh && referral.data && referral.data.status !== 'draft') {
    return <ReferralSubmissionView referral={referral.data} config={config.data} />;
  }
  return (
    <ReferralEditor
      key={session.editor}
      referral={fresh ? null : (referral.data ?? null)}
      config={config.data}
      onCreated={(newId) => {
        setCreatedId(newId);
        navigate(`/my-referrals/${newId}`, { replace: true });
      }}
    />
  );
}

function SaveStatus({ state }: { state: SaveState }) {
  const text =
    state.status === 'saving'
      ? 'Saving…'
      : state.status === 'saved'
        ? `Draft saved ${new Date(state.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
        : state.status === 'idle'
          ? 'Not saved yet'
          : null;
  return text ? (
    <Typography variant="body2" color="text.secondary" role="status" data-testid="save-status">
      {text}
    </Typography>
  ) : null;
}

function ExistingFamilyPanel({ existingReferralId }: { existingReferralId: string | null }) {
  return (
    <Alert severity="error" sx={{ mb: 2 }} role="alert" data-testid="existing-family">
      <AlertTitle>This family already has a referral</AlertTitle>
      <Typography variant="body2">
        A referral already exists for this family last name and parent/guardian email. To avoid
        duplicate records, this one can't be saved or submitted while they match.
        {existingReferralId ? ' Open the existing referral instead.' : ''}
      </Typography>
      <Stack direction="row" gap={1} mt={1.5} flexWrap="wrap">
        {existingReferralId && (
          <Button
            variant="contained"
            size="small"
            component={RouterLink}
            to={`/my-referrals/${existingReferralId}`}
          >
            View existing referral
          </Button>
        )}
        <Button variant="outlined" size="small" component={RouterLink} to="/my-referrals">
          Go to My Referrals
        </Button>
      </Stack>
    </Alert>
  );
}

function ReferralEditor({
  referral,
  config,
  onCreated,
}: {
  referral: ReferralDto | null;
  config: ReferralFormDto;
  onCreated: (id: string) => void;
}) {
  const { me } = useAuth();
  const { id: routeId = 'new' } = useParams();
  const { notify, notifyError } = useNotify();
  const qc = useQueryClient();
  const usage = useMyAllotment();
  const navigate = useNavigate();
  const submitReferral = useSubmitReferral();
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const t = useMemo(() => referralCopy(config.formCopy), [config.formCopy]);
  const [initial] = useState(() =>
    referral ? formFromReferral(referral) : newDraftForm(config.cycles),
  );
  const saver = useDraftSaver({ referral, initial, onCreated });
  const { form, edit, save, saveState } = saver;
  const [issues, setIssues] = useState<string[]>([]);
  const [reloading, setReloading] = useState(false);
  const [related, setRelated] = useState(false);
  const checkDuplicates = useCheckReferralDuplicates();
  const checkSeq = useRef(0);
  const topRef = useRef<HTMLDivElement>(null);

  const loadedCycle = referral?.cycle;
  const cycles = useMemo(() => {
    const open = config.cycles.map((c) => ({ id: c.id, label: c.label, referralsOpen: true }));
    if (
      loadedCycle &&
      form.cycleId === loadedCycle.id &&
      !open.some((c) => c.id === loadedCycle.id)
    )
      open.push({ ...loadedCycle, referralsOpen: false });
    return open;
  }, [config.cycles, loadedCycle, form.cycleId]);

  useEffect(() => {
    if (usage.error) notifyError(usage.error, 'Loading your recommendation usage failed');
  }, [usage.error, notifyError]);
  const allotment = usage.data
    ? allotmentFor(usage.data, form.cycleId, routeId === 'new' ? null : routeId)
    : null;

  const step = form.currentStep;
  const blocked = saveState.status === 'conflict' || saveState.status === 'gone';
  const scrollTop = () => topRef.current?.scrollIntoView({ block: 'start' });

  const checkRelated = (f: DraftForm) => {
    const seq = ++checkSeq.current;
    const referralId = saver.referralId();
    const { cycleId, payload } = toRequest(f);
    checkDuplicates
      .mutateAsync({ referralId, cycleId, payload })
      .then((res) => {
        if (seq === checkSeq.current) setRelated(res.possiblyRelated);
      })
      .catch((err: unknown) => {
        console.error('Checking for possibly related records failed', { referralId, err });
      });
  };

  const goTo = (target: number) => {
    if (target === step || target < 1 || target > LAST_STEP) return;
    let next = saver.formRef.current;
    if (target > step) {
      if (target > 3) next = pruneBlankChildren(next);
      const found = referralIssuesBefore(next, target, { grades: config.applyingGrades });
      if (found.length) {
        setIssues(found);
        scrollTop();
        return;
      }
      edit(() => ({ ...next, currentStep: target }));
      if (saver.worthSaving()) void save(true);
    } else {
      edit((f) => ({ ...f, currentStep: target }));
    }
    if (RELATED_CHECK_STEPS.includes(step)) checkRelated(next);
    setIssues([]);
    scrollTop();
  };

  const submit = async () => {
    const current = pruneBlankChildren(saver.formRef.current);
    const found = referralSubmitIssues(current, {
      grades: config.applyingGrades,
      cycleOpen: config.cycles.some((c) => c.id === current.cycleId),
      acknowledged,
    });
    if (found.length) {
      setIssues(found);
      scrollTop();
      return;
    }
    setSubmitting(true);
    try {
      if (!(await save(true))) return;
      const id = saver.referralId();
      if (!id) return;
      await submitReferral.mutateAsync({ id, version: saver.version(), acknowledged });
      saver.stop();
      navigate(`/my-referrals/${id}/confirmation`);
    } catch (err) {
      const existing = existingFamilyFrom(err);
      if (existing) {
        console.error('Referral submit refused: the family already has a referral', { err });
        saver.flagExistingFamily(existing);
        scrollTop();
      } else if (err instanceof ApiError && err.code === REFERRAL_INCOMPLETE) {
        console.error('Referral submit refused as incomplete', { issues: err.issues });
        setIssues(err.issues.map((i) => i.message));
        scrollTop();
      } else {
        notifyError(err, 'Submitting the referral failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reload = async () => {
    const id = saver.referralId();
    if (!id) return;
    setReloading(true);
    try {
      const latest = await qc.fetchQuery({
        queryKey: keys.myReferral(id),
        queryFn: () => get<ReferralDto>(`/me/referrals/${id}`),
        staleTime: 0,
      });
      saver.reload(latest, formFromReferral(latest));
      setIssues([]);
    } catch (err) {
      notifyError(err, 'Reloading the referral failed');
    } finally {
      setReloading(false);
    }
  };

  const StepView = STEP_VIEWS[step - 1]!;
  const props: StepProps = {
    form,
    edit,
    t,
    grades: config.applyingGrades,
    cycles,
    me,
    allotmentLimit: config.allotmentLimit,
    allotment,
    order: config.formOrder,
    hidden: config.hiddenQuestions,
  };
  const noun = recordNoun(form.countsTowardAllotment);
  const title = form.payload.familyLastName.trim()
    ? `${noun}: ${form.payload.familyLastName.trim()} family`
    : `New ${noun.toLowerCase()}`;

  return (
    <Box sx={page} ref={topRef}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        mb={2}
        flexWrap="wrap"
      >
        <Box>
          <Typography variant="h2">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            Step {step} of {LAST_STEP}: {REFERRAL_STEPS[step - 1]!.label}
          </Typography>
        </Box>
        <SaveStatus state={saveState} />
      </Stack>
      <Stepper nonLinear activeStep={step - 1} alternativeLabel sx={{ mb: 2 }}>
        {REFERRAL_STEPS.map((s) => (
          <Step key={s.id} completed={s.step < step}>
            <StepButton onClick={() => goTo(s.step)} disabled={blocked || submitting}>
              {s.label}
            </StepButton>
          </Step>
        ))}
      </Stepper>

      {saveState.status === 'conflict' && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" disabled={reloading} onClick={() => void reload()}>
              Reload latest version
            </Button>
          }
        >
          {saveState.message} Changes made here since the last save were not saved.
        </Alert>
      )}
      {saveState.status === 'existing_family' && (
        <ExistingFamilyPanel existingReferralId={saveState.existing.existingReferralId} />
      )}
      {related && (
        <Alert severity="info" sx={{ mb: 2 }} role="status" data-testid="related-notice">
          {POSSIBLY_RELATED_NOTICE}
        </Alert>
      )}
      {saveState.status === 'gone' && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" component={RouterLink} to="/my-referrals">
              My Referrals
            </Button>
          }
        >
          This draft can no longer be saved. It was deleted or submitted in another tab or window.
        </Alert>
      )}
      {saveState.status === 'error' && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void save(true)}>
              Retry
            </Button>
          }
        >
          The draft could not be saved: {describeError(saveState.error)}
        </Alert>
      )}
      {issues.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} role="alert" data-testid="review-issues">
          <AlertTitle>Please review the following</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {issues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </Box>
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h3" component="h3" mb={2}>
          {t(`step${step}_title`)}
        </Typography>
        <StepView {...props} />
        {step === LAST_STEP && (
          <Paper variant="outlined" sx={{ mt: 3, p: 2, bgcolor: 'action.hover' }}>
            <FormControlLabel
              sx={{ alignItems: 'flex-start', m: 0 }}
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  sx={{ mt: -0.75, ml: -1 }}
                />
              }
              label={<Typography variant="body2">{t('acknowledgement')}</Typography>}
            />
          </Paper>
        )}
        <Stack
          direction="row"
          justifyContent="space-between"
          gap={1}
          mt={4}
          pt={2}
          sx={{ borderTop: 1, borderColor: 'divider' }}
          flexWrap="wrap"
        >
          <Button
            variant="outlined"
            disabled={step === 1 || blocked || submitting}
            onClick={() => goTo(step - 1)}
          >
            Back
          </Button>
          <Stack direction="row" gap={1}>
            <Button
              disabled={blocked || submitting || saveState.status === 'saving'}
              onClick={() =>
                void save(true).then((ok) => {
                  if (ok) notify('success', 'Draft saved');
                })
              }
            >
              Save draft
            </Button>
            {step < LAST_STEP ? (
              <Button variant="contained" disabled={blocked} onClick={() => goTo(step + 1)}>
                Continue
              </Button>
            ) : (
              <Button
                variant="contained"
                disabled={blocked || submitting || !acknowledged}
                onClick={() => void submit()}
              >
                {submitting ? 'Submitting…' : 'Submit referral'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
